import { cn } from '@atlas/ui-primitives'
import type { ReactNode } from 'react'

/**
 * Editorial molecules for the ATLAS screens (mockup design language): a mono
 * uppercase eyebrow over a serif display title. Feature-local + storied
 * per CLAUDE.md; promote to `@atlas/ui-domain` when a 2nd feature reuses them.
 */

export interface EyebrowProps {
	children: ReactNode
	className?: string
}

/** Mono, uppercase, wide-tracked overline (brand voice: `MANIFESTO · 01_`). */
export function Eyebrow({ children, className }: EyebrowProps) {
	return (
		<p className={cn('font-mono text-ds-2xs uppercase tracking-ds-widest text-ds-text-subtle', className)}>
			{children}
		</p>
	)
}

export interface PageHeaderProps {
	eyebrow?: ReactNode
	title: ReactNode
	subtitle?: ReactNode
	/** Right-aligned actions (buttons, toggles). */
	actions?: ReactNode
	className?: string
}

/** Standard screen header: eyebrow · serif title · subtitle, with optional actions. */
export function PageHeader({ eyebrow, title, subtitle, actions, className }: PageHeaderProps) {
	return (
		<header className={cn('mb-8', className)}>
			<div className="flex items-start justify-between gap-4">
				<div className="min-w-0">
					{eyebrow ? <Eyebrow className="mb-2">{eyebrow}</Eyebrow> : null}
					<h1 className="font-ds-display text-4xl font-semibold leading-tight text-ds-text">{title}</h1>
				</div>
				{actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
			</div>
			{subtitle ? <p className="mt-3 max-w-2xl text-ds-text-muted">{subtitle}</p> : null}
		</header>
	)
}
